import React from "react";
import styled from "styled-components";
import ScrollableTableContainer from "./ScrollableTableContainer";
import LottieLoadingIndicator from "./LottieLoadingIndicator";
import { portalColors } from "./BusinessPortalComponents";

export const DataTableContainer = styled(ScrollableTableContainer)`
    max-width: ${p => p.maxWidth || "98.625rem"};
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    @media (max-width: 760px) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }
`;

export const DataTableElement = styled.table`
    width: 100%;
    min-width: ${p => p.minWidth || "760px"};
    min-height: ${p => p.minHeight || "20rem"};
    --portal-table-row-height: ${p => p.$rowHeight || "3.4rem"};
    --portal-table-cell-padding: ${p => p.$cellPadding || "0.6rem 1rem"};
    --portal-table-header-padding: ${p => p.$headerPadding || "0.85rem 1rem"};
    border-collapse: separate;
    border-spacing: 0;
    border: none;
    border-radius: 0 0 16px 16px;
    background: ${portalColors.surface};
    z-index: auto;

    @media (max-width: 760px) {
        min-width: ${p => p.mobileMinWidth || p.minWidth || "760px"};
    }
`;

export const DataTableHead = styled.thead`
    background: linear-gradient(180deg, ${portalColors.surface} 0%, ${portalColors.surfaceSoft} 100%);
`;

export const DataTableBody = styled.tbody``;

export const DataTableRow = styled.tr`
    background-color: ${p => p.selected ? "var(--calendar-selected-cell-bg, rgba(91,110,245,0.08))" : undefined};
    --data-table-cell-color: ${portalColors.text};
    --data-table-cell-background: ${p => p.selected ? "var(--calendar-selected-cell-bg, rgba(91,110,245,0.08))" : portalColors.surface};
    --data-table-cell-pointer-events: ${p => (p.$empty || p.className === "empty-row" || p.className === "placeholder-row") ? "none" : "auto"};
    --data-table-cell-empty-color: ${p => (p.$empty || p.className === "empty-row" || p.className === "placeholder-row") ? "transparent" : "inherit"};
`;

export const DataTableHeaderCell = styled.th`
    width: ${p => p.width};
    text-align: ${p => p.textAlign || "left"};
    cursor: ${p => p.onClick ? "pointer" : "default"};
    position: sticky;
    top: 0;
    z-index: 2;
    background: linear-gradient(180deg, ${portalColors.surface} 0%, ${portalColors.surfaceSoft} 100%);
    color: ${portalColors.text};
    padding: var(--portal-table-header-padding, 0.85rem 1rem);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    vertical-align: middle;
    border-color: ${portalColors.border};
    border-top: none;
    border-bottom: 1px solid ${portalColors.border};
    border-left: ${p => p.$withDivider ? `1px solid ${portalColors.border}` : "none"};
    border-right: none;
    box-shadow: none;
    border-radius: 0;

    &:not(:first-child) {
        border-left: 1px solid ${portalColors.border};
    }

    @media (max-width: 760px) {
        padding: 0.7rem 0.8rem;
    }
`;

export const DataTableHeaderCheckboxContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

export const DataTableCell = styled.td`
    text-align: ${p => p.textAlign || "left"};
    width: ${p => p.width};
    height: var(--portal-table-row-height);
    padding: ${p => p.padding || "var(--portal-table-cell-padding, 0.6rem 1rem)"} !important;
    white-space: ${p => p.whiteSpace || "nowrap"} !important;
    vertical-align: ${p => p.verticalAlign || "middle"};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${p => p.$empty ? "transparent" : `var(--data-table-cell-color, ${portalColors.text})`};
    border-color: ${portalColors.border};
    border-bottom: 1px solid ${portalColors.border};
    border-right: ${p => p.$last ? "none" : `1px solid ${portalColors.border}`};
    background: ${p => p.$empty ? "transparent" : `var(--data-table-cell-background, ${portalColors.surface})`};
    max-width: 24rem;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: ${p => p.$empty ? "none" : "var(--data-table-cell-pointer-events, auto)"};
    transition: background 0.16s ease, color 0.16s ease;

    > * {
        min-width: 0;
    }

    @media (max-width: 760px) {
        padding: ${p => p.padding || "0.7rem 0.8rem"} !important;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

export const DataTableActionButton = styled.button`
    width: ${p => p.width || "auto"};
    height: ${p => p.height || "32px"};
    min-width: ${p => p.minWidth || "6.25rem"};
    padding: ${p => p.padding || "0 0.8rem"};
    background: ${p => p.backgroundColor || portalColors.surfaceSoft};
    color: ${p => p.color || portalColors.text};
    border: ${p => p.border || `1px solid ${portalColors.borderStrong}`};
    border-radius: ${p => p.radius || "8px"};
    box-shadow: ${p => p.boxShadow || "none"};
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.fontSize || "0.75rem"};
    font-weight: ${p => p.fontWeight || 800};
    line-height: ${p => p.lineHeight || "1"};
    opacity: ${p => p.disabled ? 0.65 : 1};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    white-space: nowrap;
    transition: transform 0.16s ease, background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease;

    &:hover {
        transform: translateY(-1px);
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover {
            transform: none;
        }
    }
`;

const compactTableActionButtonProps = {
    width: "101px",
    height: "32px",
    minWidth: "101px",
    radius: "7px",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "14px",
    padding: "0 0.7rem"
};

export function DataTableEditActionButton({
    children = "Edit",
    backgroundColor = "var(--calendar-surface, #FFFFFF)",
    color = "var(--calendar-text, #171A22)",
    border = "1px solid var(--calendar-text, #171A22)",
    boxShadow = "0px 3px 6px #00000029",
    ...props
}) {
    return (
        <DataTableActionButton
            {...compactTableActionButtonProps}
            {...props}
            backgroundColor={backgroundColor}
            color={color}
            border={border}
            boxShadow={boxShadow}
        >
            {children}
        </DataTableActionButton>
    );
}

export function DataTableViewActionButton({
    children = "View",
    backgroundColor = "var(--calendar-dark-ink, #171A22)",
    color = "var(--calendar-dark-control-text, #FFFFFF)",
    border = "1px solid var(--calendar-dark-ink, #171A22)",
    boxShadow = "0px 3px 6px #00000029",
    ...props
}) {
    return (
        <DataTableActionButton
            {...compactTableActionButtonProps}
            {...props}
            backgroundColor={backgroundColor}
            color={color}
            border={border}
            boxShadow={boxShadow}
        >
            {children}
        </DataTableActionButton>
    );
}

const DataTableLoadingContainer = styled.div`
    width: 100%;
    min-height: ${p => p.minHeight || "24rem"};
    display: grid;
    place-items: center;
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    box-shadow: ${portalColors.shadow};
    box-sizing: border-box;
`;

function renderDataTableChild(child) {
    if (!React.isValidElement(child)) {
        return child;
    }

    const children = React.Children.map(child.props.children, renderDataTableChild);

    if (child.type === "thead") {
        return <DataTableHead {...child.props}>{children}</DataTableHead>;
    }

    if (child.type === "tbody") {
        return <DataTableBody {...child.props}>{children}</DataTableBody>;
    }

    if (child.type === "tr") {
        return <DataTableRow {...child.props}>{children}</DataTableRow>;
    }

    return React.cloneElement(child, undefined, children);
}

export function DataTable({ children, minWidth, minHeight, mobileMinWidth, rowHeight, cellPadding, headerPadding, ...props }) {
    return (
        <DataTableElement
            minWidth={minWidth}
            minHeight={minHeight}
            mobileMinWidth={mobileMinWidth}
            $rowHeight={rowHeight}
            $cellPadding={cellPadding}
            $headerPadding={headerPadding}
            {...props}
        >
            {React.Children.map(children, renderDataTableChild)}
        </DataTableElement>
    );
}

export function DataTableLoading({ minHeight }) {
    return (
        <DataTableLoadingContainer minHeight={minHeight}>
            <LottieLoadingIndicator />
        </DataTableLoadingContainer>
    );
}

export default DataTable;
